import React, { Component } from "react";
import { ContentWrapper, Table, Icon } from "components";
import { Row, Col, Input, notification, Select } from "antd";
import { DateTime } from "luxon";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { API, Endpoints } from "utils/api";
import Strings from "utils/strings";
import { push } from "connected-react-router";
import { translate } from "utils/utils";
import "./styles.scss";

class WorkshopPartDetail extends Component<any, any> {
	constructor(props: any) {
		super(props);

		this.state = {
			part: null,
			movements: [],
			hasUnsavedFields: false,
			isNew: props.match?.params?.id === "new",
			partNumber: "",
			name: "",
			instructions: "",
			unit: "un",
			quantity: 0,
			preferredSupplier: null,
			suppliers: [],
			language: "pt"
		};
	}

	componentDidMount(): void {
		delayedDispatch(
			setBreadcrumb(() => {
				const { hasUnsavedFields, language } = this.state;
				return {
					locations: [
						{
							text: Strings.workshop.parts,
							route: "/workshop/parts",
							icon: "tools"
						},
						{
							text: this.state.isNew
								? Strings.workshop.newPart
								: this.state.partNumber || this.state.name || Strings.generic.loading
						}
					],
					actions: [
						{
							type: "language",
							value: language,
							onChange: (value: any) => {
								const part = this.state.part || {};
								this.setState({
									language: value,
									name: part.name?.[value] || part.name?.pt || part.name?.en || "",
									instructions: part.instructions?.[value] || part.instructions?.pt || part.instructions?.en || ""
								});
							},
							className: "BreadcrumbLanguage",
							margin: "left"
						},
						{
							type: "button",
							text: Strings.generic.save,
							onClick: () => this.submitPart(),
							disabled: !hasUnsavedFields,
							className: hasUnsavedFields ? "BreadcrumbButtonSuccess" : "",
							separator: "left",
							isSave: true
						}
					]
				};
			})
		);

		this.getData();
		this.getSuppliers();
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;

		delayedDispatch(
			setBreadcrumb(() => {
				const { hasUnsavedFields, language } = this.state;
				return {
					locations: [
						{
							text: Strings.workshop.parts,
							route: "/workshop/parts",
							icon: "tools"
						},
						{
							text: this.state.isNew
								? Strings.workshop.newPart
								: this.state.partNumber || this.state.name || Strings.generic.loading
						}
					],
					actions: [
						{
							type: "language",
							value: language,
							onChange: (value: any) => {
								const part = this.state.part || {};
								this.setState({
									language: value,
									name: part.name?.[value] || part.name?.pt || part.name?.en || "",
									instructions: part.instructions?.[value] || part.instructions?.pt || part.instructions?.en || ""
								});
							},
							className: "BreadcrumbLanguage",
							margin: "left"
						},
						{
							type: "button",
							text: Strings.generic.save,
							onClick: () => this.submitPart(),
							disabled: !hasUnsavedFields,
							className: hasUnsavedFields ? "BreadcrumbButtonSuccess" : "",
							separator: "left",
							isSave: true
						}
					]
				};
			})
		);
		dispatch(updateCrumb());
	}

	async getData() {
		const { isNew } = this.state;
		const { dispatch, match } = this.props;

		if (isNew) {
			return dispatch(setTitle(Strings.workshop.newPart));
		}

		dispatch(setLoader(true));

		try {
			const [partResponse, movementsResponse] = await Promise.all([
				API.get({
					url: Endpoints.uriWorkshopParts(match?.params?.id)
				}),
				API.get({
					url: Endpoints.uriWorkshopPartMovements(match?.params?.id)
				})
			]);

			if (partResponse.ok) {
				const part = partResponse.data?.results?.part;
				dispatch(setTitle(part?.partNumber || translate(part?.name) || Strings.generic.loading));
				const preferredSupplierId = part?.preferredSupplier?._id 
					? String(part.preferredSupplier._id)
					: (part?.preferredSupplier && typeof part.preferredSupplier === 'string' 
						? String(part.preferredSupplier) 
						: null);
				// Remove preferredSupplier from part to avoid spreading the object
				const { preferredSupplier: _, ...partWithoutSupplier } = part || {};
				this.setState({
					...partWithoutSupplier,
					part,
					partNumber: part?.partNumber || "",
					name: part?.name?.pt || part?.name?.en || "",
					instructions: part?.instructions?.pt || part?.instructions?.en || "",
					unit: part?.unit || "un",
					quantity: part?.quantity || 0,
					averageCost: part?.averageCost || 0,
					preferredSupplier: preferredSupplierId
				});
			}

			if (movementsResponse.ok) {
				this.setState({ movements: movementsResponse.data?.results?.movements || [] });
			}
		} catch (err) {
			console.log("API Error", err);
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async getSuppliers() {
		try {
			const response = await API.get({
				url: Endpoints.uriSuppliers()
			});

			if (response.ok) {
				this.setState({ suppliers: response.data?.results?.supplier || [] });
			}
		} catch (err) {
			console.log("API Error", err);
		}
	}

	async submitPart() {
		const { partNumber, name, instructions, unit, preferredSupplier, isNew, language } = this.state;
		const { dispatch, match } = this.props;

		if (!partNumber?.trim() || !name?.trim()) {
			return notification.warn({
				message: Strings.workshop.part,
				description: Strings.errors.invalidFields,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(true));

		try {
			const request = !isNew ? API.put : API.post;
			const part = this.state.part || {};
			const response = await request({
				url: Endpoints.uriWorkshopParts((!isNew && match?.params?.id) || ""),
				data: {
					partNumber: partNumber?.toUpperCase() || "",
					name: {
						...(part.name || {}),
						[language]: name
					},
					instructions: {
						...(part.instructions || {}),
						[language]: instructions
					},
					unit,
					preferredSupplier: preferredSupplier || undefined
				}
			});

			if (response.ok && response.data.results?.part) {
				if (isNew) {
					return dispatch(push("/workshop/parts"));
				}

				const updatedPart = response.data.results.part;
				this.setState({
					hasUnsavedFields: false,
					part: updatedPart,
					partNumber: updatedPart?.partNumber || "",
					name: updatedPart?.name?.[language] || updatedPart?.name?.pt || updatedPart?.name?.en || "",
					instructions: updatedPart?.instructions?.[language] || updatedPart?.instructions?.pt || updatedPart?.instructions?.en || "",
					unit: updatedPart?.unit || "un",
					quantity: updatedPart?.quantity || 0,
					preferredSupplier: updatedPart?.preferredSupplier?._id 
						? String(updatedPart.preferredSupplier._id)
						: (updatedPart?.preferredSupplier && typeof updatedPart.preferredSupplier === 'string' 
							? String(updatedPart.preferredSupplier) 
							: null)
				});

				dispatch(setTitle(updatedPart?.partNumber || translate(updatedPart?.name) || ""));

				notification.success({
					message: Strings.workshop.part,
					description: response.data.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async recalculateStock() {
		const { dispatch, match } = this.props;

		dispatch(setLoader(true));

		try {
			const response = await API.patch({
				url: Endpoints.uriPartsRecalculateStock(),
				data: { partId: match?.params?.id }
			});

			if (response.ok) {
				await this.getData();
				notification.success({
					message: Strings.workshop.part,
					description: response.data?.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	getMovementType(type: string) {
		if (type === "purchase") return Strings.workshop.purchase;
		if (type === "intervention") return Strings.workshop.intervention;
		return type;
	}

	renderInfo() {
		const { partNumber, name, instructions, unit, quantity, averageCost, preferredSupplier, suppliers } = this.state;

		const unitOptions = [
			{ value: 'un', label: 'Unidade (un)' },
			{ value: 'l', label: 'Litro (l)' },
			{ value: 'kg', label: 'Quilograma (kg)' },
			{ value: 'm', label: 'Metro (m)' },
			{ value: 'm2', label: 'Metro Quadrado (m²)' },
			{ value: 'ml', label: 'Metro Linear (ml)' }
		];

		return (
			<ContentWrapper extraStyle={{ padding: 20 }}>
				<div className="WorksheetTitle" style={{ paddingBottom: 30 }}>
					<Icon name="frame" />
					<p>{Strings.worksheets.generalInfo}</p>
				</div>
				<Row gutter={[20, 10]}>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="part_number" className="InputLabel --label-required">
							{Strings.workshop.partNumber}
						</label>
						<Input
							id="part_number"
							placeholder={Strings.workshop.partNumber}
							value={partNumber || ""}
							onChange={(event: any) => {
								this.setState({ partNumber: event.target.value, hasUnsavedFields: true });
							}}
							onBlur={(event: any) => {
								const value = event.target.value?.toUpperCase() || "";
								this.setState({ partNumber: value, hasUnsavedFields: true });
							}}
						/>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="part_unit" className="InputLabel --label-required">
							{Strings.workshop.unit}
						</label>
						<Select
							id="part_unit"
							className="tagsSelector"
							style={{ width: "100%" }}
							placeholder={Strings.workshop.unit}
							value={unit || 'un'}
							onChange={(value: any) => {
								this.setState({ unit: value, hasUnsavedFields: true });
							}}
						>
							{unitOptions.map((option) => (
								<Select.Option key={option.value} value={option.value}>
									{option.label}
								</Select.Option>
							))}
						</Select>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="name" className="InputLabel --label-required">
							{Strings.workshop.name}
						</label>
						<Input
							id="name"
							placeholder={Strings.workshop.name}
							value={name || ""}
							onChange={(event: any) => {
								this.setState({ name: event.target.value, hasUnsavedFields: true });
							}}
						/>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="averageCost" className="InputLabel">
							{Strings.workshop.averageCost}
						</label>
						<Input
							id="averageCost"
							value={averageCost != null ? `${averageCost.toFixed(2)} €` : "0.00 €"}
							disabled
							readOnly
						/>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="preferredSupplier" className="InputLabel">
							{Strings.workshop.preferredSupplier}
						</label>
						<Select
							id="preferredSupplier"
							className="tagsSelector"
							style={{ width: "100%" }}
							placeholder={Strings.workshop.preferredSupplier}
							showSearch
							filterOption={(input: any, option: any) =>
								option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							value={preferredSupplier || null}
							onChange={(value: any) => {
								this.setState({ preferredSupplier: value, hasUnsavedFields: true });
							}}
							allowClear
						>
							{suppliers
								.filter((s: any) => s.isActive !== false)
								.map((supplier: any) => (
									<Select.Option key={supplier._id} value={supplier._id}>
										{supplier.name || "-"}
									</Select.Option>
								))}
						</Select>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="quantity" className="InputLabel">
							{Strings.workshop.inStock}
						</label>
						<Input
							id="quantity"
							type="number"
							disabled
							value={quantity || 0}
							placeholder={Strings.workshop.quantity}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="instructions" className="InputLabel">
							{Strings.workshop.instructions}
						</label>
						<Input.TextArea
							id="instructions"
							placeholder={Strings.workshop.instructions}
							value={instructions || ""}
							rows={4}
							onChange={(event: any) => {
								this.setState({ instructions: event.target.value, hasUnsavedFields: true });
							}}
						/>
					</Col>
				</Row>
			</ContentWrapper>
		);
	}

	renderMovements() {
		const { movements } = this.state;

		return (
			<ContentWrapper>
				<Table
					title={{
						icon: "tools",
						title: Strings.workshop.movements
					}}
					headerOptions={[{
						label: Strings.workshop.recalculateStock,
						tooltip: Strings.workshop.recalculateStock,
						icon: "refresh",
						onClick: () => this.recalculateStock()
					}]}
					data={movements}
					columns={[
						{
							Header: Strings.workshop.date,
							id: "date",
							accessor: (row: any) =>
								row.date && DateTime.fromISO(row.date).toFormat("dd/MM/yyyy")
						},
						{
							Header: Strings.workshop.type,
							id: "type",
							accessor: (row: any) => this.getMovementType(row.type) || "-"
						},
						{
							Header: Strings.workshop.quantity,
							id: "quantity",
							accessor: (row: any) => row.quantity || 0
						},
						{
							Header: Strings.workshop.value,
							id: "value",
							align: "right",
							accessor: (row: any) => (row.value && `${row.value.toFixed(2)} €`) || "-"
						},
						{
							Header: Strings.workshop.supplier,
							id: "supplier",
							accessor: (row: any) => row.supplier || "-"
						},
						{
							Header: Strings.workshop.licensePlate,
							id: "licensePlate",
							accessor: (row: any) => row.licensePlate || "-"
						}
					]}
					filterable
					paginated={false}
				/>
			</ContentWrapper>
		);
	}

	render(): JSX.Element {
		const { isNew } = this.state;

		return (
			<React.Fragment>
				<Helmet>
					<title>
						{Strings.workshop.part} -{" "}
						{isNew ? Strings.workshop.newPart : this.state.partNumber || this.state.name || Strings.generic.loading}
					</title>
					<meta name="description" content="Description of Workshop Part" />
				</Helmet>
				<div className="WorksheetScreen" style={{ paddingBottom: 80 }}>
					{this.renderInfo()}
					{!isNew && this.renderMovements()}
				</div>
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language
});

export default connect(mapStateToProps)(WorkshopPartDetail);

